import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';

import { MeetingRepository } from 'repository/meetingRepository';
import { FlashRepository } from 'repository/flashRepository';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { isNativeApp, openNativePlaceExplore, openExternalUrl } from 'common/webview';
import { useUserLocation } from 'hooks/useUserLocation';
import { LocationLoading } from 'components/common/LocationLoading';
import config from 'config/config';
import { PlaceMapView, MapContainer } from 'components/map';
import { MeetingProgress } from 'components/meeting/MeetingProgress';
import { useTranslation } from 'react-i18next';
import { useSkipIfCreated } from 'hooks/useSkipIfCreated';
import { IcCall, IcBolt, IcStar as IcStarFilled, IcStarOutline } from 'components/common/icons/EmojiIcons';
import { CategoryIcon } from 'components/common/icons/categoryIcon';
import {
    AppScreen, AppBar, Section, SectionHeader, Badge, Button, ButtonFooter, CardRail, PlaceCard, EmptyState, InfoGrid, ActionRow, ActionList, SwitchList, SwitchRow, Sheet, FilterBar, Bleed,
} from 'components/ds';
import { haversineMeters } from 'common/utils/geo';
import styles from './PlaceExplore.module.css';
import { fetchPlacePhotos, isPlaceholderPhoto } from 'common/utils/placePhotos';
import { distanceKm } from 'common/utils/geo';
import { toUtcISOString } from 'common/utils/datetime';
import { PhotoCarousel } from 'components/ds';
import { SponsoredPlaces, SponsoredPlace } from 'components/meeting/SponsoredPlaces';

const STORAGE_KEY = 'meeting_create_data';

/* =========================================================
   인라인 SVG 아이콘 — 이모지 금지 (feedback_no_emoji.md)
   ========================================================= */
const SvgIcon: React.FC<{ d?: React.ReactNode; className?: string; size?: number; strokeWidth?: number }>
    = ({ d, className, size = 18, strokeWidth = 1.8 }) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        {d}
    </svg>
);

const IcPin = (props: any) => (
    <SvgIcon {...props} d={<>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </>} />
);
const IcClock = (props: any) => (
    <SvgIcon {...props} d={<>
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
    </>} />
);
const IcSearch = (props: any) => (
    <SvgIcon {...props} d={<>
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.6" y2="16.6" />
    </>} />
);
const IcChevron = (props: any) => (
    <SvgIcon {...props} d={<polyline points="6 9 12 15 18 9" />} strokeWidth={2} />
);
const IcReview = (props: any) => (
    <SvgIcon {...props} d={<>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </>} />
);

// 카테고리/장소 아이콘은 categoryIcon 키 → CategoryIcon 컴포넌트로 렌더 (categoryIcon.tsx 참조). 이모지 금지.

interface Place {
    id: string;
    placeId?: string; // 원본 placeId (photos API용)
    name: string;
    address: string;
    category?: string;
    categories?: string[]; // Google Places types 전체
    categoryIcon?: string;
    categoryLabel?: string;
    lat?: number;
    lng?: number;
    imageUrl?: string;
    photos?: string[];     // 다중 사진 (백엔드에서 photos[] 제공 시 사용)
    rating?: number;
    reviewCount?: number;
    distance?: string;
    lastUsed?: string;
    visitCount?: number;
    openingHours?: string;        // 기존(요약) 호환
    openingHoursList?: string[];  // 요일별 영업시간 (백엔드에서 제공 시)
    website?: string;
    phone?: string;
    description?: string;
}

// 카테고리 ID/아이콘 (라벨은 t()로 동적 생성)
// 일반 모임 카테고리
// icon 은 카테고리 키 (CategoryIcon 컴포넌트가 SVG 로 렌더). 이모지 금지.
const NORMAL_CATEGORY_IDS = [
    { id: 'all', icon: 'all' },
    { id: 'restaurant', icon: 'restaurant' },
    { id: 'bar', icon: 'bar' },
    { id: 'cafe', icon: 'cafe' },
    { id: 'activity', icon: 'activity' },
    { id: 'sports', icon: 'sports' },
];

// 여행 모임 카테고리
const TRAVEL_CATEGORY_IDS = [
    { id: 'all', icon: 'all' },
    { id: 'attraction', icon: 'attraction' },
    { id: 'restaurant', icon: 'restaurant' },
    { id: 'cafe', icon: 'cafe' },
    { id: 'shopping', icon: 'shopping' },
    { id: 'activity', icon: 'activity' },
];

// 숙박 전용 모드 — v6 상세 [+ 숙박 등록] 흐름에서 진입 (location.state.hotelOnlyMode=true)
const HOTEL_ONLY_CATEGORY_IDS = [
    { id: 'all', icon: 'all' },
    { id: 'hotel', icon: 'hotel' },
];


/** 섹션당 처음 보여 줄 개수 — 84장을 한 번에 쏟으면 고르기가 더 어렵습니다 */
const SECTION_PREVIEW = 5;
/* 레일 한 줄에 미리 깔아 두는 장수 — 넘기다 끝나면 「더 보기」로 이어 받습니다 */
const RAIL_PREVIEW = 12;
/** 사진을 한 번에 받아 올 개수 — 서버 상한이 20 */
const PHOTO_BATCH = 20;

export const PlaceExplore = () => {
    useSkipIfCreated();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { showAlert, showConfirm } = useAlert();
    const { t } = useTranslation();
    const savedMeetingData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    /* 모임 생성 중이면 임시 저장값으로, 이미 만들어진 모임의 장소를 고르러
       들어왔으면 넘겨받은 값으로 판단합니다. 예전에는 임시 저장값만 봐서
       여행 모임인데도 명소 분류가 빠진 목록이 떴습니다. */
    const isTravel = savedMeetingData.meetingType === 'TRAVEL'
        || !!(location.state as any)?.isTravelMeeting;
    const destinationInfo = savedMeetingData.destinationInfo;
    // 숙박 전용 모드 — SubPlaceModal 에서 [+ 숙박 등록] 로 진입 시 카테고리·추천을 숙박업소로만 필터
    const hotelOnlyMode = !!(location.state as any)?.hotelOnlyMode;
    const CATEGORY_IDS = hotelOnlyMode
        ? HOTEL_ONLY_CATEGORY_IDS
        : (isTravel ? TRAVEL_CATEGORY_IDS : NORMAL_CATEGORY_IDS);
    const CATEGORIES = CATEGORY_IDS.map(c => ({
        ...c,
        label: t(`place.category.${c.id}`, { defaultValue: c.id }),
    }));
    const [isCreating, setIsCreating] = useState(false);
    // 모임 생성 확인 모달
    const [showCreateConfirm, setShowCreateConfirm] = useState(false);
    const [pendingCreatePlace, setPendingCreatePlace] = useState<Place | null>(null);
    // 🏁 도착 레이스 설정 (기본: 꺼짐)
    const [raceEnabled, setRaceEnabled] = useState(false);
    const [lateFeeAmount, setLateFeeAmount] = useState(5000);
    const [shuttlePenaltyEnabled, setShuttlePenaltyEnabled] = useState(true); // 호환
    const [lastPlacePenaltyType, setLastPlacePenaltyType] = useState<'NONE' | 'SHUTTLE' | 'SHOT' | 'BOTH'>('SHUTTLE');

    // 급번개 후보 추가 모드 (/flash/add-place/:token) — 단일 선택, 비회원 가능
    const flashAddToken: string | undefined = (params as any)?.token;
    const isFlashAddMode = !!flashAddToken;
    const [flashAddExisting, setFlashAddExisting] = useState<{ externalPlaceId?: string; placeName?: string }[]>([]);
    const [isAddingFlashCandidate, setIsAddingFlashCandidate] = useState(false);

    // flash-add 진입 시 기존 후보 로드 (제외용)
    useEffect(() => {
        if (!isFlashAddMode || !flashAddToken) return;
        FlashRepository.onGetByToken(flashAddToken)
            .then(res => {
                const data = res?.data?.data || res?.data;
                const cands = data?.candidates || [];
                setFlashAddExisting(cands.map((c: any) => ({
                    externalPlaceId: c.externalPlaceId || undefined,
                    placeName: c.placeName,
                })));
            })
            .catch(() => { /* 무시 */ });
    }, [isFlashAddMode, flashAddToken]);

    const isExcludedFlashCand = useCallback((p: Place): boolean => {
        if (!isFlashAddMode || flashAddExisting.length === 0) return false;
        return flashAddExisting.some(ex => {
            if (ex.externalPlaceId && p.placeId && ex.externalPlaceId === p.placeId) return true;
            if (ex.placeName && p.name && ex.placeName === p.name) return true;
            return false;
        });
    }, [isFlashAddMode, flashAddExisting]);

    const handleAddFlashCandidate = useCallback(async (place: Place) => {
        if (!flashAddToken || isAddingFlashCandidate) return;
        const ok = await showConfirm({
            title: t('flash.addPlaceConfirm.title'),
            message: t('flash.addPlaceConfirm.message', { place: place.name }),
            confirmText: t('flash.addPlaceConfirm.ok'),
            cancelText: t('flash.addPlaceConfirm.cancel'),
        });
        if (!ok) return;
        setIsAddingFlashCandidate(true);
        try {
            await FlashRepository.onPostAddCandidate(flashAddToken, {
                placeName: place.name,
                placeAddress: place.address,
                latitude: place.lat,
                longitude: place.lng,
                externalPlaceId: place.placeId,
                imageUrl: place.imageUrl,
                addedByName: localStorage.getItem('flash_voter_name') || undefined,
            });
            // 추진자(FlashStatus)에서 진입한 경우 returnTo 로 복귀, 그 외에는 투표 화면으로.
            const returnTo = (location.state as any)?.returnTo;
            navigate(returnTo || `/flash/vote/${flashAddToken}`, { replace: true });
        } catch (e: any) {
            showAlert({ message: e?.response?.data?.message || t('flash.addPlaceConfirm.failed') });
        } finally {
            setIsAddingFlashCandidate(false);
        }
    }, [flashAddToken, isAddingFlashCandidate, navigate, showAlert, t]);

    // 급번개 다중 선택 모드 (?mode=flash)
    const isFlashMode = new URLSearchParams(location.search).get('mode') === 'flash' && !isFlashAddMode;
    const FLASH_MAX = 5;
    const [flashSelected, setFlashSelected] = useState<Place[]>(() => {
        // FlashCreate에서 돌아올 때 state로 전달된 기존 선택 복원
        const incoming = (location.state as any)?.flashCandidates;
        if (Array.isArray(incoming)) {
            return incoming.map((c: any) => ({
                id: c.externalPlaceId || `flash-${c.placeName}`,
                placeId: c.externalPlaceId,
                name: c.placeName,
                address: c.placeAddress || '',
                lat: c.latitude,
                lng: c.longitude,
                imageUrl: c.imageUrl,
            } as Place));
        }
        return [];
    });

    // 동일 장소 매칭 — id 가 리스트별로 다른 포맷이므로(최근방문=UUID / 근거리=placeId-type / 복귀=externalPlaceId)
    // externalPlaceId → placeId → id → (이름 + 좌표 근접) 순으로 식별.
    const flashMatches = (a: Place, b: Place): boolean => {
        const aExt = (a as any).externalPlaceId || a.placeId;
        const bExt = (b as any).externalPlaceId || b.placeId;
        if (aExt && bExt && aExt === bExt) return true;
        if (a.placeId && b.placeId && a.placeId === b.placeId) return true;
        if (a.id && b.id && a.id === b.id) return true;
        // 외부 ID 둘 다 없는 경우 — 이름 동일 & 좌표 ≈ 30m 이내
        if (
            a.name && b.name && a.name === b.name &&
            typeof a.lat === 'number' && typeof b.lat === 'number' &&
            typeof a.lng === 'number' && typeof b.lng === 'number' &&
            Math.abs(a.lat - b.lat) < 0.0003 &&
            Math.abs(a.lng - b.lng) < 0.0003
        ) {
            return true;
        }
        return false;
    };

    const isFlashSelected = (place: Place): boolean =>
        isFlashMode && flashSelected.some(p => flashMatches(p, place));

    const toggleFlashSelection = (place: Place) => {
        setFlashSelected(prev => {
            const existsIdx = prev.findIndex(p => flashMatches(p, place));
            if (existsIdx >= 0) return prev.filter((_, i) => i !== existsIdx);
            if (prev.length >= FLASH_MAX) {
                showAlert({ message: t('placeExplore.alertMaxSelect', { max: FLASH_MAX }) });
                return prev;
            }
            return [...prev, place];
        });
    };

    const proceedFlash = () => {
        if (flashSelected.length < 1) {
            showAlert({ message: t('placeExplore.alertMinSelect') });
            return;
        }
        navigate('/flash/create', {
            state: {
                flashCandidates: flashSelected.map(p => ({
                    placeName: p.name,
                    placeAddress: p.address,
                    latitude: p.lat,
                    longitude: p.lng,
                    externalPlaceId: p.placeId,
                    imageUrl: p.imageUrl,
                })),
            },
        });
    };

    const [selectedPlaceForConfirm, setSelectedPlaceForConfirm] = useState<Place | null>(null);

    // 외부 지도앱 공유 흐름 — sessionStorage 의 selectedPlaceInfo 가 있으면 진입 즉시 모임 생성 확인 모달.
    // 일반/플래시 모드 외(flash 후보 등록 / 다른 흐름) 에서만 트리거.
    useEffect(() => {
        if (isFlashMode || isFlashAddMode || location.state?.subPlaceMode) return;
        try {
            const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
            const info = saved.selectedPlaceInfo;
            if (info && typeof info.lat === 'number' && typeof info.lng === 'number' && info.lat !== 0 && info.lng !== 0) {
                const sharedPlace: Place = {
                    id: 'shared-place',
                    name: info.name || saved.selectedPlace || '공유된 장소',
                    address: info.address || '',
                    lat: info.lat,
                    lng: info.lng,
                    categoryIcon: 'pin',
                    categoryLabel: t('placeExplore.sharedPlace'),
                };
                console.log('[PlaceExplore] sharedPlace 자동 선택:', sharedPlace);
                // 즉시 확인 모달 ("이 장소로 모임 만들기") 표시
                setPendingCreatePlace(sharedPlace);
                setShowCreateConfirm(true);
            } else {
                console.log('[PlaceExplore] sharedPlace 없음 — 일반 흐름', { saved });
            }
        } catch (e) { console.warn('[PlaceExplore] sessionStorage 파싱 실패:', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [showReviews, setShowReviews] = useState(false);
    const [detailOpen, setDetailOpen] = useState(true);
    const [showHoursDetail, setShowHoursDetail] = useState(false);
    /* 고르는 손잡이 — 지금 무엇으로 좁히고 있는지 화면에 계속 띄워 둡니다.
       예전에는 카테고리 칩만 있어서 "가까운 곳"과 "평 좋은 곳"을 갈라 볼 수 없었습니다. */
    const [sortBy, setSortBy] = useState<'near' | 'rating'>('near');
    /* 섹션마다 처음에는 몇 개만 보여 주고 [더 보기] 로 펼칩니다.
       84장을 한 번에 쏟으면 고르기가 더 어렵습니다. */
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    /* 스폰서 장소 — 광고 소스가 붙으면 여기에 채웁니다.
       지금은 비어 있어 SponsoredPlaces 가 아무것도 그리지 않습니다. */
    const [sponsoredPlaces] = useState<SponsoredPlace[]>([]);
    const [showSortSheet, setShowSortSheet] = useState(false);
    /* 거리를 어디에서 재고 있는지. 숙박·이전 장소 기준으로 볼 때가 있어
       "1.2km" 만 보여 주면 어디서부터인지 알 수 없습니다. */
    const anchorName = (() => {
        const st = location.state as any;
        if (st?.subPlaceMode && st?.hotelCenter) return t('placeExplore.subPlaceAnchorHotel');
        if (st?.subPlaceMode && st?.prevSubPlaceCenter) return t('placeExplore.subPlaceAnchorPrev');
        return t('placeExplore.anchorMe');
    })();
    const dragStartY = useRef(0);
    const dragStartX = useRef(0);
    const dragCurrentY = useRef(0);
    const dragCurrentX = useRef(0);
    const cardElRef = useRef<HTMLDivElement>(null);
    const isDragDisabled = useRef(false);
    const [reviews, setReviews] = useState<any[]>([]);
    const [isReviewLoading, setIsReviewLoading] = useState(false);
    const [mapMode, setMapMode] = useState(false);
    const [mapSelectedPlace, setMapSelectedPlace] = useState<Place | null>(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    // 사진 갤러리 라이트박스 (카드 사진 탭 시 — 지도 이동 대신 사진 보기)
    // 카카오맵 스타일 필터 칩 — 정렬/영업중/평점/리뷰수
    const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [locationName, setLocationName] = useState(t('placeExplore.checkingLocation'));
    const [recentPlaces, setRecentPlaces] = useState<Place[]>([]);
    const [allNearbyPlaces, setAllNearbyPlaces] = useState<Place[]>([]);
    const [nearbyPlaces, setNearbyPlaces] = useState<Place[]>([]);
    // rev5 · 참여자가 자주 간 곳 (모임 컨텍스트에서만) — 일반모임 섹션 2
    const [frequentPlaces, setFrequentPlaces] = useState<Place[]>([]);
    const [isNearbyLoading, setIsNearbyLoading] = useState(true); // GPS 대기 중에도 로딩 표시
    const [locationFailed, setLocationFailed] = useState(false);   // 위치 획득 실패 상태 (fallback 대신 명시적 표시)
    const photoLoadedRef = useRef<Set<string>>(new Set()); // 이미 사진 요청한 placeId 추적
    const allNearbyPlacesRef = useRef<Place[]>([]);        // 최신 allNearbyPlaces (stale closure 방지)

    const { location: cachedLocation, fetchLocation: fetchUserLocation } = useUserLocation();
    // 캐시된 위치가 없을 때만 로딩바 표시
    const isLocationLoading = !cachedLocation && !currentLocation && !isTravel && !locationFailed;

    // 현재 위치 가져오기 (캐시 우선 → 여행은 여행지 중심)
    // subPlace 추가 모드는 anchor 우선순위 — 이전 sub_place > 호텔 > 여행지 > 사용자 GPS
    useEffect(() => {
        // subPlaceMode anchor 우선순위 — 모임에 이미 추가된 장소들 기준 검색
        if (location.state?.subPlaceMode) {
            const prev = (location.state as any).prevSubPlaceCenter as { lat: number; lng: number } | null | undefined;
            const hotel = (location.state as any).hotelCenter as { lat: number; lng: number } | null | undefined;
            const dest = (location.state as any).travelDestinationCenter as { lat: number; lng: number } | null | undefined;
            const stateIsTravel = !!(location.state as any).isTravelMeeting;
            const stateHotelOnly = !!(location.state as any).hotelOnlyMode;

            // 숙박 전용 모드는 여행지 anchor 를 최우선 — 사용자 GPS 가 한국이어도 일본 여행지 근처
            // 숙박이 검색되어야 하고, 이전 sub_place(다른 관광지) 나 이전 호텔 근처가 아닌
            // 여행지 중심을 기준으로 다양한 숙박을 보여줘야 함
            if (stateHotelOnly && dest?.lat && dest?.lng) {
                setCurrentLocation({ lat: dest.lat, lng: dest.lng });
                setLocationName(t('placeExplore.travelDefault'));
                return;
            }

            // 1순위: 이전 장소 좌표 (있으면 무조건) — 숙박 전용은 위에서 이미 처리
            if (prev?.lat && prev?.lng) {
                setCurrentLocation({ lat: prev.lat, lng: prev.lng });
                setLocationName(t('placeExplore.subPlaceAnchorPrev'));
                return;
            }
            // 2순위: 여행 모임 → 호텔 좌표
            if (stateIsTravel && hotel?.lat && hotel?.lng) {
                setCurrentLocation({ lat: hotel.lat, lng: hotel.lng });
                setLocationName(t('placeExplore.subPlaceAnchorHotel'));
                return;
            }
            // 3순위: 여행 모임 → 여행지 좌표
            if (stateIsTravel && dest?.lat && dest?.lng) {
                setCurrentLocation({ lat: dest.lat, lng: dest.lng });
                setLocationName(t('placeExplore.travelDefault'));
                return;
            }
            // 4순위(일반 모임 또는 위 모두 실패) — 아래 사용자 GPS / 캐시 흐름으로 fallthrough
        }

        if (isTravel && destinationInfo?.lat && destinationInfo?.lng) {
            const travelCenter = { lat: destinationInfo.lat, lng: destinationInfo.lng };
            setCurrentLocation(travelCenter);
            setLocationName(destinationInfo.name || destinationInfo.country || t('placeExplore.travelDefault'));
            return;
        }

        // 캐시된 위치가 있으면 즉시 사용 — 백그라운드 갱신만
        if (cachedLocation) {
            setCurrentLocation({ lat: cachedLocation.lat, lng: cachedLocation.lng });
            setLocationFailed(false);
            // 캐시 만료 여부는 fetchLocation 내부에서 판단하고 갱신
            fetchUserLocation().then(loc => {
                if (loc && (loc.lat !== cachedLocation.lat || loc.lng !== cachedLocation.lng)) {
                    setCurrentLocation({ lat: loc.lat, lng: loc.lng });
                }
            });
            return;
        }

        // 캐시가 없으면 awaiting fetch
        // - 네이티브 앱: 위치 획득 실패 시 사용자가 권한 부여하도록 재시도 버튼 노출 (currentLocation null 유지)
        // - 브라우저: 권한 거부/미허용이 흔해 검색 자체가 막히지 않도록 서울 시청을 디폴트로 fallback
        const getLocation = async () => {
            const loc = await fetchUserLocation();
            if (loc) {
                setCurrentLocation({ lat: loc.lat, lng: loc.lng });
                setLocationFailed(false);
            } else if (!isNativeApp()) {
                // 브라우저 — 서울 시청 좌표 디폴트
                setCurrentLocation({ lat: 37.5663, lng: 126.9779 });
                setLocationFailed(false);
            } else {
                // 네이티브 — UI에서 재시도 버튼 노출
                setLocationFailed(true);
                setIsNearbyLoading(false);
            }
        };
        getLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 사용자가 수동으로 위치 재시도
    const handleRetryLocation = useCallback(async () => {
        setLocationFailed(false);
        setIsNearbyLoading(true);
        const loc = await fetchUserLocation(true); // force refresh
        if (loc) {
            setCurrentLocation({ lat: loc.lat, lng: loc.lng });
        } else if (!isNativeApp()) {
            // 브라우저 — 권한이 여전히 거부됐어도 검색이 막히지 않도록 시청 fallback 유지
            setCurrentLocation({ lat: 37.5663, lng: 126.9779 });
        } else {
            setLocationFailed(true);
            setIsNearbyLoading(false);
        }
    }, [fetchUserLocation]);

    // 현재 위치 역지오코딩
    useEffect(() => {
        if (!currentLocation || !window.kakao?.maps) return;
        try {
            const geocoder = new kakao.maps.services.Geocoder();
            geocoder.coord2Address(currentLocation.lng, currentLocation.lat, (result, status) => {
                if (status === 'OK' && result[0]) {
                    const addr = result[0].address;
                    setLocationName(`${addr.region_2depth_name} ${addr.region_3depth_name}`);
                }
            });
        } catch {
            setLocationName(t('placeExplore.myArea'));
        }
    }, [currentLocation]);

    // 최근 사용 장소 조회 + 사진/평점 보강 (무료 placePhotos 엔드포인트 사용)
    // 단, 비회원이 접근하는 flash-add 모드에서는 스킵 (인증 필요)
    useEffect(() => {
        if (isFlashAddMode) {
            return;
        }
        const fetchRecent = async () => {
            try {
                const res = await instance.get(`${getApiUrl('meetings')}/places/recent`, {
                    params: { limit: 10 }
                });
                if (res?.data?.data) {
                    // externalPlaceId는 추천장소(travel_place_cache)와의 매칭용 — 내부 placeId와 별도
                    const places: (Place & { externalPlaceId?: string })[] = res.data.data.map((p: any) => ({
                        id: p.placeId,
                        placeId: p.placeId,
                        externalPlaceId: p.externalPlaceId || undefined,
                        name: p.placeName,
                        address: p.placeAddress,
                        lat: parseFloat(p.latitude),
                        lng: parseFloat(p.longitude),
                        lastUsed: p.lastUsedDate,
                        visitCount: typeof p.visitCount === 'number' ? p.visitCount : undefined,
                    }));
                    setRecentPlaces(places);

                    // 사진/평점/영업시간 일괄 보강 — externalPlaceId로 placePhotos 호출
                    // externalPlaceId가 없는 장소(구버전 데이터)는 요청에서 제외
                    const idMap: Record<string, string> = {}; // externalId → 내부 placeId (매핑용)
                    const extIds: string[] = [];
                    places.forEach(p => {
                        const ext = p.externalPlaceId;
                        if (ext) {
                            extIds.push(ext);
                            idMap[ext] = p.placeId!;
                        }
                    });
                    if (extIds.length > 0) {
                        try {
                            const data = await fetchPlacePhotos(extIds);
                            if (Object.keys(data).length > 0) {
                                setRecentPlaces(prev => prev.map(p => {
                                    const ext = (p as any).externalPlaceId;
                                    const pd = ext ? data[ext] : undefined;
                                    return pd ? {
                                        ...p,
                                        imageUrl: pd.photoUrl || p.imageUrl,
                                        photos: pd.photos && pd.photos.length > 0 ? pd.photos : p.photos,
                                        rating: pd.rating ?? p.rating,
                                        openingHours: pd.openingHours || p.openingHours,
                                        openingHoursList: pd.openingHoursList || p.openingHoursList,
                                    } : p;
                                }));
                            }
                        } catch { /* 사진 없어도 무시 */ }
                    }
                }
            } catch { /* 실패 시 빈 배열 유지 */ }
        };
        fetchRecent();
    }, []);

    // rev5 · 참여자가 자주 간 곳 (모임 컨텍스트에서만) — 일반모임 섹션 2
    useEffect(() => {
        const mId = (location.state as any)?.meetingId;
        if (!mId || isFlashAddMode || isFlashMode) { setFrequentPlaces([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res: any = await MeetingRepository.onGetFrequentPlaces(mId, 10);
                const raw = res?.data?.data || res?.data || [];
                const list: Place[] = (Array.isArray(raw) ? raw : []).map((p: any) => ({
                    id: p.placeId || p.externalPlaceId || p.placeName || p.name,
                    placeId: p.placeId,
                    name: p.placeName || p.name || '',
                    address: p.placeAddress || p.address || '',
                    lat: parseFloat(p.latitude ?? p.lat) || 0,
                    lng: parseFloat(p.longitude ?? p.lng) || 0,
                    imageUrl: p.photoUrl || p.imageUrl,
                    rating: typeof p.rating === 'number' ? p.rating : undefined,
                    categoryLabel: p.categoryLabel || p.category,
                    visitCount: typeof p.visitCount === 'number' ? p.visitCount : undefined,
                } as Place)).filter((p: Place) => p.name);
                if (!cancelled) setFrequentPlaces(list);
            } catch { if (!cancelled) setFrequentPlaces([]); }
        })();
        return () => { cancelled = true; };
    }, [isFlashAddMode, isFlashMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // 카테고리 키 → 카테고리 키 (CategoryIcon 이 키 → SVG 매핑). 이모지 금지.
    // (categoryIcon 필드에 카테고리 키를 그대로 넣음. CategoryIcon 이 매핑 처리.)
    const categoryIcons: Record<string, string> = {
        restaurant: 'restaurant', cafe: 'cafe', bar: 'bar', meal_delivery: 'meal_delivery',
        meal_takeaway: 'meal_takeaway', bakery: 'bakery', amusement_park: 'amusement_park',
        gym: 'gym', sports: 'sports', movie_theater: 'movie_theater', museum: 'museum',
        park: 'park', spa: 'spa', nightlife: 'nightlife', entertainment: 'entertainment',
        activity: 'activity', karaoke: 'karaoke', bowling: 'bowling',
        attraction: 'attraction', shopping: 'shopping',
        hotel: 'hotel', lodging: 'hotel', accommodation: 'hotel',
        default: 'pin',
    };

    const categoryLabels: Record<string, string> = {
        restaurant: t('placeExplore.restaurant'), cafe: t('placeExplore.cafe'), bar: t('placeExplore.bar'), bakery: t('placeExplore.bakery'),
        meal_delivery: t('placeExplore.delivery'), meal_takeaway: t('placeExplore.takeaway'),
        amusement_park: t('placeExplore.amusement'), gym: t('placeExplore.gym'), sports: t('placeExplore.sports'),
        movie_theater: t('placeExplore.movie'), museum: t('placeExplore.museum'), park: t('placeExplore.park'), spa: t('placeExplore.spa'),
        nightlife: t('placeExplore.nightlife'), entertainment: t('placeExplore.entertainment'), activity: t('placeExplore.entertainment'),
        karaoke: t('placeExplore.karaoke'), bowling: t('placeExplore.bowling'),
        attraction: t('placeExplore.attraction'), shopping: t('placeExplore.shopping'),
        hotel: t('placeExplore.hotel'),
        lodging: t('placeExplore.hotel'),
        accommodation: t('placeExplore.hotel'),
    };

    // 카테고리 필터 매핑
    const categoryFilterMap: Record<string, string[]> = {
        all: [],
        restaurant: ['restaurant', 'meal_delivery', 'meal_takeaway', 'bakery'],
        cafe: ['cafe', 'bakery', 'coffee'],
        bar: ['bar', 'night_club', 'liquor_store', 'nightlife'],
        attraction: ['attraction', 'tourist_attraction', 'museum', 'landmark', 'park'],
        shopping: ['shopping', 'mall', 'market'],
        activity: ['amusement_park', 'movie_theater', 'museum', 'park', 'bowling_alley', 'entertainment', 'karaoke'],
        sports: ['gym', 'stadium', 'spa', 'sports'],
    };

    // 최초 1회: 전체 주변 장소 로드 (위치 변경 시에만 재호출)
    useEffect(() => {
        if (!currentLocation) return;

        const fetchAllNearby = async () => {
            // 로컬 캐시 확인 (5분 유효) - 새로고침 시 즉시 표시
            const cacheKey = `nearby_${currentLocation.lat.toFixed(2)}_${currentLocation.lng.toFixed(2)}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const { data, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < 5 * 60 * 1000) {
                        // 캐시 데이터에서 Foursquare 아이콘 URL 제거 (빨간 핀 방지)
                        const cleaned = data.map((p: Place) => ({
                            ...p,
                            imageUrl: isPlaceholderPhoto(p.imageUrl) ? undefined : p.imageUrl,
                            category: categoryLabels[p.category || ''] ? p.category : 'restaurant',
                            categoryLabel: categoryLabels[p.category || ''] || '음식점',
                            categoryIcon: categoryIcons[p.category || ''] || categoryIcons.default,
                        }));
                        setAllNearbyPlaces(cleaned);
                        setIsNearbyLoading(false);
                        return;
                    }
                } catch { /* 캐시 파싱 실패 시 무시 */ }
            }

            setIsNearbyLoading(true);

            try {
                // 모임 유형에 따라 타입별 우선순위/요청 수 조정 — 빠진 가게 줄이기 위해 limit/radius 확대
                const typeConfigs = hotelOnlyMode
                    ? [  // 숙박 전용 모드: 숙박업소만
                        { type: 'hotel', limit: 25 },
                        { type: 'lodging', limit: 15 },
                        { type: 'accommodation', limit: 15 },
                    ]
                    : isTravel
                    ? [  // 여행 모임: 관광지/명소 우선
                        { type: 'attraction', limit: 25 },
                        { type: 'restaurant', limit: 20 },
                        { type: 'cafe', limit: 15 },
                        { type: 'entertainment', limit: 12 },
                        { type: 'shopping', limit: 12 },
                    ]
                    : [  // 일반 모임: 술집/음식점/카페 우선
                        { type: 'restaurant', limit: 25 },
                        { type: 'bar', limit: 20 },
                        { type: 'cafe', limit: 20 },
                        { type: 'entertainment', limit: 12 },
                        { type: 'sports', limit: 12 },
                    ];

                // 병렬 호출
                const results = await Promise.allSettled(
                    typeConfigs.map(({ type, limit }) =>
                        instance.get(getApiUrl('nearbyPlaces'), {
                            params: { lat: currentLocation.lat, lng: currentLocation.lng, type, radius: 5000, limit }
                        }).then(res => {
                            const data = res?.data?.data || [];
                            const source = data.length > 0 && data[0].placeId?.startsWith('kakao-') ? '카카오' : 'Foursquare';
                            console.log(`[Nearby] ${type}: ${data.length}건 (${source})`);
                            return { type, data };
                        })
                    )
                );

                const allResults: Place[] = [];
                const addedKeys = new Set<string>(); // 카테고리+이름 기준 중복 방지
                for (const result of results) {
                    if (result.status !== 'fulfilled') continue;
                    const { type, data } = result.value;
                    for (const p of data) {
                        const mainType = type;
                        const key = `${mainType}-${p.name}`;
                        if (addedKeys.has(key)) continue;
                        addedKeys.add(key);

                        const placeId = p.placeId || p.name;
                        allResults.push({
                            id: `${placeId}-${mainType}`,
                            placeId: placeId, // 원본 placeId (photos API용)
                            name: p.name,
                            address: p.address || '',
                            category: mainType,
                            categories: [mainType, ...(p.types || [])],
                            categoryIcon: categoryIcons[mainType] || categoryIcons.default,
                            categoryLabel: p.categoryLabel || categoryLabels[mainType] || mainType,
                            lat: p.lat,
                            lng: p.lng,
                            rating: p.rating || undefined,
                            reviewCount: p.userRatingsTotal || 0,
                            // nearby 가 준 사진(일본=Hotpepper/OTM 등)을 즉시 사용 → /photos 응답 전에도 바로 노출.
                            // Foursquare 카테고리 아이콘(4sqi.net/img/categories)은 사진 아님 → 제외.
                            imageUrl: isPlaceholderPhoto(p.photoUrl) ? undefined : p.photoUrl,
                            openingHours: p.openNow !== undefined ? (p.openNow ? t('placeExplore.open') : t('placeExplore.closed')) : undefined,
                        });
                    }
                }

                if (allResults.length > 0) {
                    const unique = allResults.filter((p, i, arr) =>
                        arr.findIndex(a => a.name === p.name) === i
                    );

                    // 정렬 — subPlaceMode 일 때는 이전 장소 거리 → 호텔 거리 → 평점
                    // 일반 모드 (모임 생성)는 평점 높은 순 (기존 동작)
                    const subMode = !!location.state?.subPlaceMode;
                    const prev = subMode ? ((location.state as any).prevSubPlaceCenter as { lat: number; lng: number } | null | undefined) : null;
                    const hotel = subMode ? ((location.state as any).hotelCenter as { lat: number; lng: number } | null | undefined) : null;
                    const haversineKm = distanceKm;

                    if (subMode && (prev || hotel)) {
                        unique.sort((a, b) => {
                            const aPos = (typeof a.lat === 'number' && typeof a.lng === 'number') ? { lat: a.lat, lng: a.lng } : null;
                            const bPos = (typeof b.lat === 'number' && typeof b.lng === 'number') ? { lat: b.lat, lng: b.lng } : null;
                            if (prev) {
                                const aD = aPos ? haversineKm(prev, aPos) : Infinity;
                                const bD = bPos ? haversineKm(prev, bPos) : Infinity;
                                if (aD !== bD) return aD - bD;
                            }
                            if (hotel) {
                                const aD = aPos ? haversineKm(hotel, aPos) : Infinity;
                                const bD = bPos ? haversineKm(hotel, bPos) : Infinity;
                                if (aD !== bD) return aD - bD;
                            }
                            return (b.rating || 0) - (a.rating || 0);
                        });
                        // 카드에 표시할 거리(이전 장소 기준) 텍스트 주입
                        if (prev) {
                            unique.forEach(p => {
                                if (typeof p.lat === 'number' && typeof p.lng === 'number') {
                                    const km = haversineKm(prev, { lat: p.lat, lng: p.lng });
                                    p.distance = km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
                                }
                            });
                        }
                    } else {
                        // 평점 높은 순 정렬
                        unique.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                    }
                    console.log(`[Nearby] 총 ${allResults.length}건 → 중복제거 후 ${unique.length}건`);
                    setAllNearbyPlaces(unique);
                    setIsNearbyLoading(false);

                    // sessionStorage에 캐시 저장
                    try {
                        sessionStorage.setItem(cacheKey, JSON.stringify({ data: unique, timestamp: Date.now() }));
                    } catch { /* storage full 등 무시 */ }

                    // 화면에 그려질 만큼 사진을 미리 받습니다.
                    // 예전에는 3건만 받아 나머지가 빈 네모로 남았습니다(84장 중 23장만 사진).
                    const firstPlaces = unique.slice(0, PHOTO_BATCH).filter(p => p.placeId);
                    const firstIds = firstPlaces.map(p => p.placeId!);
                    console.log('[Photos] 요청 ID:', firstIds);
                    if (firstIds.length > 0) {
                        firstIds.forEach(id => photoLoadedRef.current.add(id));
                        try {
                            const photos = await fetchPlacePhotos(firstIds);
                            if (Object.keys(photos).length > 0) {
                                setAllNearbyPlaces(prev => {
                                    const updated = prev.map(p => {
                                        const pid = p.placeId || p.id;
                                        const pd = photos[pid];
                                        return pd ? {
                                            ...p,
                                            imageUrl: pd.photoUrl || p.imageUrl,
                                            photos: pd.photos && pd.photos.length > 0 ? pd.photos : p.photos,
                                            rating: pd.rating ?? p.rating,
                                            openingHours: pd.openingHours || p.openingHours,
                                            openingHoursList: pd.openingHoursList || p.openingHoursList,
                                        } : p;
                                    });
                                    // 캐시도 갱신
                                    try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: updated, timestamp: Date.now() })); } catch {}
                                    return updated;
                                });
                            }
                        } catch {
                            firstIds.forEach(id => photoLoadedRef.current.delete(id));
                        }
                    }
                    return;
                }
            } catch { /* fallback */ }

            // Geoapify fallback
            if (config.GEOAPIFY_KEY) {
                try {
                    const geoRes = await fetch(
                        `https://api.geoapify.com/v2/places?categories=catering,entertainment,leisure,tourism.sights&filter=circle:${currentLocation.lng},${currentLocation.lat},3000&limit=30&apiKey=${config.GEOAPIFY_KEY}`
                    );
                    const geoData = await geoRes.json();
                    if (geoData?.features) {
                        setAllNearbyPlaces(geoData.features.map((f: any, idx: number) => {
                            const p = f.properties;
                            const subCat = (p.categories || [])[0]?.split('.')?.pop() || '';
                            return {
                                id: p.place_id || `geo-${idx}`,
                                name: p.name || '이름 없음',
                                address: p.address_line2 || p.formatted || '',
                                category: subCat,
                                categoryIcon: categoryIcons[subCat] || categoryIcons.default,
                                categoryLabel: categoryLabels[subCat] || subCat,
                                lat: f.geometry.coordinates[1],
                                lng: f.geometry.coordinates[0],
                            };
                        }).filter((p: Place) => p.name !== '이름 없음'));
                    }
                } catch { /* fallback도 실패 */ }
            }

            setIsNearbyLoading(false);
        };

        fetchAllNearby();
    }, [currentLocation]); // 위치 변경 시에만 재호출

    // 카테고리 변경 시 프론트에서 필터링 (category 필드 기준)
    useEffect(() => {
        if (selectedCategory === 'all') {
            setNearbyPlaces(allNearbyPlaces);
        } else {
            const filterTypes = categoryFilterMap[selectedCategory] || [];
            const filtered = allNearbyPlaces.filter(p => {
                return p.category && filterTypes.includes(p.category);
            });
            console.log(`[Filter] ${selectedCategory} → filterTypes:`, filterTypes, `→ ${filtered.length}건 (전체 ${allNearbyPlaces.length}건)`);
            if (filtered.length === 0) {
                console.log('[Filter] 매칭 실패 - 장소들의 category 값:', allNearbyPlaces.slice(0, 5).map(p => ({ name: p.name, category: p.category, categories: p.categories })));
            }
            setNearbyPlaces(filtered);
        }
    }, [selectedCategory, allNearbyPlaces]);

    // allNearbyPlaces ref 동기화 (lazy fetch 클로저용)
    useEffect(() => {
        allNearbyPlacesRef.current = allNearbyPlaces;
    }, [allNearbyPlaces]);

    // 지도 모드 진입 시 history entry 푸시 → 뒤로가기는 추천장소 화면으로 복귀
    useEffect(() => {
        if (!mapMode) return;
        window.history.pushState({ placeExploreMapMode: true }, '');
        const onPop = (e: PopStateEvent) => {
            // 지도 모드 닫기 (페이지 이탈 방지)
            setMapMode(false);
            setSelectedPlaceForConfirm(null);
            setShowReviews(false);
            setDetailOpen(true);
            e.stopImmediatePropagation?.();
        };
        window.addEventListener('popstate', onPop);
        return () => {
            window.removeEventListener('popstate', onPop);
            // 정상 복귀 (mapMode=false 되어 cleanup 실행) 시 우리가 push한 entry를 제거
            if (window.history.state?.placeExploreMapMode) {
                window.history.back();
            }
        };
    }, [mapMode]);

    // 보이는 장소들의 사진을 배치 요청 (화면 id → 원본 placeId 변환)
    // 주의: allNearbyPlaces는 setter functional update + ref로 최신값을 읽음 (stale closure 회피)
    const fetchPhotosForPlaces = useCallback(async (visibleIds: string[]) => {
        const newIds = visibleIds.filter(id => !photoLoadedRef.current.has(id));
        if (newIds.length === 0) return;

        newIds.forEach(id => photoLoadedRef.current.add(id));

        // 화면 id에서 원본 placeId 추출 — ref를 통해 최신 allNearbyPlaces 읽기
        const latest = allNearbyPlacesRef.current;
        const origIds = latest
            .filter(p => newIds.includes(p.id) && p.placeId)
            .map(p => p.placeId!);
        if (origIds.length === 0) {
            // 실패한 id는 다음 시도가 가능하도록 ref에서 제거
            newIds.forEach(id => photoLoadedRef.current.delete(id));
            return;
        }

        try {
            const photos = await fetchPlacePhotos(origIds);

            if (Object.keys(photos).length > 0) {
                setAllNearbyPlaces(prev => {
                    const updated = prev.map(p => {
                        const pid = p.placeId || p.id;
                        const pd = photos[pid];
                        return pd ? {
                            ...p,
                            imageUrl: pd.photoUrl || p.imageUrl,
                            photos: pd.photos && pd.photos.length > 0 ? pd.photos : p.photos,
                            rating: pd.rating ?? p.rating,
                            openingHours: pd.openingHours || p.openingHours,
                            openingHoursList: pd.openingHoursList || p.openingHoursList,
                        } : p;
                    });
                    // 캐시 갱신
                    if (currentLocation) {
                        const ck = `nearby_${currentLocation.lat.toFixed(2)}_${currentLocation.lng.toFixed(2)}`;
                        try { sessionStorage.setItem(ck, JSON.stringify({ data: updated, timestamp: Date.now() })); } catch {}
                    }
                    return updated;
                });
            }
        } catch {
            newIds.forEach(id => photoLoadedRef.current.delete(id));
        }
    }, [currentLocation]);

    // rev5 · 가로 스크롤 섹션이라 스크롤 옵저버 대신 로드된 주변 장소 사진을 선제 요청.
    //   (fetchPhotosForPlaces 는 photoLoadedRef 로 중복 제거 → 반복 호출 안전. 이전 재기획에서
    //    옵저버가 걸려있던 세로 리스트를 제거하면서 주변 사진 로드가 끊긴 회귀 수정.)
    useEffect(() => {
        if (nearbyPlaces.length === 0) return;
        const ids = nearbyPlaces.map(p => p.id).filter(Boolean);
        if (ids.length > 0) fetchPhotosForPlaces(ids);
    }, [nearbyPlaces, fetchPhotosForPlaces]);

    // 장소 변경 시 사진 인덱스/영업시간 펼침 리셋
    useEffect(() => {
        setShowHoursDetail(false);
    }, [selectedPlaceForConfirm?.id]);

    // 확인 모달에서 보이는 장소(선택+양옆) 사진 프리페치 — 스크롤 이벤트가 발생 안 하는 모달 스와이프 케이스
    useEffect(() => {
        if (!selectedPlaceForConfirm) return;
        const places = nearbyPlaces.filter(p => p.name);
        const idx = places.findIndex(p => p.id === selectedPlaceForConfirm.id);
        if (idx < 0) return;
        const targets = [places[idx - 1], places[idx], places[idx + 1]]
            .filter((p): p is Place => !!p && !!p.id)
            .map(p => p.id);
        if (targets.length > 0) fetchPhotosForPlaces(targets);
    }, [selectedPlaceForConfirm, nearbyPlaces, fetchPhotosForPlaces]);

    // 좌우 스와이프로 이전/다음 장소
    const switchPlace = (direction: 'prev' | 'next') => {
        if (!selectedPlaceForConfirm) return;
        const places = nearbyPlaces.filter(p => p.name);
        const idx = places.findIndex(p => p.id === selectedPlaceForConfirm.id);
        const newIdx = direction === 'next' ? idx + 1 : idx - 1;
        if (newIdx >= 0 && newIdx < places.length) {
            const newPlace = places[newIdx];
            setSelectedPlaceForConfirm(newPlace);
            setMapSelectedPlace(newPlace);
            setShowReviews(false);
            fetchReviews(newPlace);
        }
    };

    // 장소 카드 클릭 → 지도 모드 전환 + 장소 선택 + 리뷰 팝업
    const handleSelectPlace = async (place: Place) => {
        // 급번개 후보 추가 모드 — 단일 선택 → 즉시 후보 등록
        if (isFlashAddMode) {
            handleAddFlashCandidate(place);
            return;
        }
        // 급번개 다중 선택 모드 — 확인 모달 안 열고 체크만 토글
        if (isFlashMode) {
            toggleFlashSelection(place);
            return;
        }

        const isKoreaLocation = currentLocation && currentLocation.lat >= 33 && currentLocation.lat <= 39 && currentLocation.lng >= 124.5 && currentLocation.lng <= 130;

        // 해외 + 네이티브 → 네이티브 PlaceExploreModal
        if (!isKoreaLocation && isNativeApp()) {
            // rev4 · mode 결정 — hotelOnlyMode(숙박) > isTravel(관광) > default(general)
            const stateHotelOnly = !!(location.state as any)?.hotelOnlyMode;
            const stateIsTravel = !!(location.state as any)?.isTravelMeeting;
            const nativeMode: 'hotel' | 'attraction' | 'general' = stateHotelOnly
                ? 'hotel'
                : stateIsTravel
                    ? 'attraction'
                    : 'general';
            const result = await openNativePlaceExplore({
                places: nearbyPlaces.filter(p => p.lat && p.lng).map(p => ({
                    id: p.id, name: p.name, address: p.address || '', lat: p.lat!, lng: p.lng!,
                    imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
                })),
                center: currentLocation || { lat: place.lat || 37.5665, lng: place.lng || 126.978 },
                confirmButtonText: location.state?.subPlaceMode ? t('placeExplore.selectPlace') : t('placeExplore.createWithPlace'),
                title: location.state?.subPlaceMode ? t('subPlace.title') : t('placeExplore.title'),
                apiUrl: config.API_URL,
                mode: nativeMode,
            });
            if (result) {
                createMeetingAndNavigate({
                    id: 'native-selected', name: result.name, address: result.address, lat: result.lat, lng: result.lng,
                });
            }
            return;
        }

        // 한국 또는 브라우저 → 웹뷰 지도 모드
        setMapSelectedPlace(place);
        setSelectedPlaceForConfirm(place);
        setDetailOpen(true);
        setMapMode(true);
        fetchReviews(place);
    };

    const handleOpenMap = (place: Place) => {
        setSelectedPlaceForConfirm(null);
        setShowReviews(false);
        setMapSelectedPlace(place);
        setMapMode(true);
    };

    // 리뷰 가져오기 — 사이드 시트 안에 토글로 들어있어, 자동으로 펼치진 않음 (사용자가 직접 클릭)
    const fetchReviews = async (place: Place) => {
        setIsReviewLoading(true);
        setReviews([]);
        try {
            const userLocale = navigator.language || 'ko';
            const res = await instance.get(getApiUrl('travelReviews'), {
                params: {
                    name: place.name,
                    address: place.address,
                    lat: place.lat,
                    lng: place.lng,
                    limit: 5,
                    locale: userLocale
                }
            });
            setReviews(res?.data?.data || []);
        } catch {
            setReviews([]);
        } finally {
            setIsReviewLoading(false);
        }
    };

    // 모임 생성 진입점 — 특수 모드 우회 후 일반 모드는 확인 모달을 먼저 표시
    const createMeetingAndNavigate = async (place?: Place | null) => {
        // 서브장소 모드: 모임 생성 안 하고 SubPlaceModal로 복귀 (확인 모달 없음)
        if (location.state?.subPlaceMode) {
            const returnTo = location.state?.returnTo || '/';
            navigate(returnTo, {
                replace: true,
                state: {
                    fromSubPlaceModal: true,
                    placeInfo: place ? { name: place.name, address: place.address, lat: place.lat, lng: place.lng } : undefined,
                    subPlaceForm: location.state?.subPlaceForm,
                    editingSubPlaceId: location.state?.editingSubPlaceId,
                    isCustomVisitTime: location.state?.isCustomVisitTime,
                    meetingId: location.state?.meetingId,
                }
            });
            return;
        }

        // 지난 모임 다시 만들기 모드: 모임 생성 안 하고 홈으로 선택된 장소만 전달
        if (location.state?.mode === 'recreate') {
            const returnTo = location.state?.returnTo || '/home';
            navigate(returnTo, {
                replace: true,
                state: {
                    selectedPlaceInfo: place ? {
                        name: place.name,
                        address: place.address,
                        lat: place.lat,
                        lng: place.lng,
                        externalPlaceId: place.placeId || place.id,
                    } : null,
                }
            });
            return;
        }

        // 일반 모드 → 리뷰/지도 팝업 먼저 닫고 확인 모달 표시
        setSelectedPlaceForConfirm(null);
        setShowReviews(false);
        setMapMode(false);
        setPendingCreatePlace(place || null);
        setShowCreateConfirm(true);
    };

    // 확인 모달 "만들기" 버튼 → 실제 모임 생성 API 호출
    const confirmCreateMeeting = async () => {
        const place = pendingCreatePlace;
        setShowCreateConfirm(false);

        if (isCreating) return;
        setIsCreating(true);

        const allData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

        try {
            // 시간 입력이 있으면 날짜와 결합해 startDatetime 계산 → createData 에 포함
            // (백엔드가 자동으로 schedule 생성 + confirmed_schedule_id 설정함)
            let startDatetimeStr: string | undefined;
            let endDatetimeStr: string | undefined;
            if (allData.selectedDate && allData.selectedTime) {
                const meetingDate = new Date(allData.selectedDate);
                const timeParts = allData.selectedTime.match(/(오전|오후)\s*(\d+):(\d+)/);
                if (timeParts) {
                    let hours = parseInt(timeParts[2]);
                    const minutes = parseInt(timeParts[3]);
                    if (timeParts[1] === '오후' && hours !== 12) hours += 12;
                    else if (timeParts[1] === '오전' && hours === 12) hours = 0;
                    meetingDate.setHours(hours, minutes, 0, 0);
                }
                // 사용자가 선택한 종료 시간 우선, 없으면 +2시간
                let endDate = new Date(meetingDate);
                if (allData.selectedEndTime) {
                    const endParts = allData.selectedEndTime.match(/(오전|오후)\s*(\d+):(\d+)/);
                    if (endParts) {
                        let eh = parseInt(endParts[2]);
                        const em = parseInt(endParts[3]);
                        if (endParts[1] === '오후' && eh !== 12) eh += 12;
                        else if (endParts[1] === '오전' && eh === 12) eh = 0;
                        endDate = new Date(allData.selectedDate);
                        endDate.setHours(eh, em, 0, 0);
                        if (endDate <= meetingDate) endDate.setDate(endDate.getDate() + 1);
                    }
                } else {
                    endDate.setHours(endDate.getHours() + 2);
                }
                startDatetimeStr = toUtcISOString(meetingDate);
                endDatetimeStr = toUtcISOString(endDate);
            }

            // 1단계: 모임 생성 (startDatetime 포함 시 백엔드가 schedule 도 자동 생성)
            const createData: any = {
                meetingName: allData.meetingName || t('placeExplore.defaultMeetingName'),
                description: allData.description || '',
                category: 'ETC',
                maxMembers: 10,
                isPublic: true,
                isRegularMeeting: allData.isRegularMeeting || false,
                repeatType: allData.repeatType || null,
                repeatInterval: allData.repeatInterval || null,
                repeatDaysOfWeek: allData.repeatDaysOfWeek || null,
                repeatDayOfMonth: allData.repeatDayOfMonth || null,
                repeatTime: allData.repeatTime || null,
            };
            if (startDatetimeStr && endDatetimeStr) {
                createData.startDatetime = startDatetimeStr;
                createData.endDatetime = endDatetimeStr;
            }

            // 🏁 도착 레이스 설정
            if (raceEnabled) {
                createData.raceEnabled = true;
                createData.lateFeeAmount = lateFeeAmount;
                createData.shuttlePenaltyEnabled = shuttlePenaltyEnabled;
                createData.lastPlacePenaltyType = lastPlacePenaltyType;
            }

            if (place) {
                createData.placeName = place.name;
                createData.placeAddress = place.address;
                createData.latitude = place.lat || 0;
                createData.longitude = place.lng || 0;
                if (place.placeId) createData.externalPlaceId = place.placeId;
            }

            const meetingResponse = await MeetingRepository.onPostCreateMeeting(createData);
            const resData = meetingResponse?.data?.data || meetingResponse?.data;
            const meetingId = typeof resData === 'string' ? resData : resData?.meetingId;
            const inviteCode = typeof resData === 'object' ? resData?.inviteCode : undefined;
            const confirmedScheduleId = typeof resData === 'object' ? resData?.confirmedScheduleId : undefined;

            if (!meetingId) {
                await showAlert({ message: t('placeExplore.alertCreateFailed') });
                setIsCreating(false);
                return;
            }

            // 2단계: 백엔드가 스케줄을 자동 생성하지 않은 경우에만 fallback 으로 생성
            // (이전 버전 백엔드 호환 — 신버전은 응답에 confirmedScheduleId 포함)
            if (!confirmedScheduleId && startDatetimeStr && endDatetimeStr) {
                const scheduleRes = await ScheduleRepository.onPostCreateSchedule({
                    meetingId,
                    startDatetime: startDatetimeStr,
                    endDatetime: endDatetimeStr,
                });
                const scheduleId = typeof scheduleRes?.data === 'string'
                    ? scheduleRes.data
                    : scheduleRes?.data?.data || scheduleRes?.data?.scheduleId;
                if (scheduleId && typeof scheduleId === 'string') {
                    await ScheduleRepository.onPostConfirmSchedule(scheduleId);
                    await MeetingRepository.onPutUpdateMeeting(meetingId, {
                        ...createData,
                        confirmedScheduleId: scheduleId,
                    });
                }
            }

            // 완료 → share (sessionStorage 데이터를 state로 전달 후 삭제)
            const shareData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
            sessionStorage.removeItem(STORAGE_KEY);
            navigate('/meeting/share', {
                replace: true,
                state: {
                    meeting: { meetingId, inviteCode: inviteCode || '' },
                    meetingName: shareData.meetingName || createData.meetingName || '',
                    selectedDate: shareData.selectedDate || '',
                    selectedTime: shareData.selectedTime || '',
                    selectedPlace: shareData.selectedPlace || shareData.selectedPlaceInfo?.name || createData.placeName || '',
                    meetingType: shareData.meetingType || createData.meetingType || 'DAY',
                    travelDestination: shareData.travelDestination || createData.travelDestination || '',
                    travelStartDate: shareData.travelStartDate || createData.travelStartDate || '',
                    travelEndDate: shareData.travelEndDate || createData.travelEndDate || '',
                    hotelName: shareData.hotelName || createData.hotelName || '',
                }
            });

        } catch (error) {
            console.error('모임 생성 실패:', error);
            await showAlert({ message: t('placeExplore.alertCreateFailed') });
        } finally {
            setIsCreating(false);
        }
    };

    // 스킵 (장소 미정)
    const handleSkip = () => {
        createMeetingAndNavigate(null);
    };

    // 지도 모드 진입 (해외+네이티브 → 네이티브, 그 외 → 웹뷰)
    const isKoreaLocation = currentLocation && currentLocation.lat >= 33 && currentLocation.lat <= 39 && currentLocation.lng >= 124.5 && currentLocation.lng <= 130;

    const openMapOrNative = async () => {
        if (!isKoreaLocation && isNativeApp()) {
            // rev4-F · mode 결정 로직 재사용 (L1044 동일 로직)
            const stateHotelOnly = !!(location.state as any)?.hotelOnlyMode;
            const stateIsTravel = !!(location.state as any)?.isTravelMeeting;
            const nativeMode: 'hotel' | 'attraction' | 'general' = stateHotelOnly
                ? 'hotel'
                : stateIsTravel
                    ? 'attraction'
                    : 'general';
            const result = await openNativePlaceExplore({
                places: nearbyPlaces.filter(p => p.lat && p.lng).map(p => ({
                    id: p.id, name: p.name, address: p.address || '', lat: p.lat!, lng: p.lng!,
                    imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
                })),
                center: currentLocation || { lat: 37.5665, lng: 126.978 },
                confirmButtonText: location.state?.subPlaceMode ? t('placeExplore.selectPlace') : t('placeExplore.createWithPlace'),
                title: location.state?.subPlaceMode ? t('subPlace.title') : t('placeExplore.title'),
                apiUrl: config.API_URL,
                mode: nativeMode,
            });
            if (result) {
                createMeetingAndNavigate({
                    id: 'native-selected', name: result.name, address: result.address, lat: result.lat, lng: result.lng,
                });
            }
            return;
        }
        setMapMode(true);
    };

    // 직접 검색 → 지도 모드
    const handleDirectSearch = () => {
        openMapOrNative();
    };

    // 최근 방문일 포맷
    const formatLastUsed = (dateStr?: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return t('placeExplore.recentToday');
        if (diffDays === 1) return t('placeExplore.recentYesterday');
        if (diffDays < 7) return t('placeExplore.recentDaysAgo', { n: diffDays });
        if (diffDays < 30) return t('placeExplore.recentWeeksAgo', { n: Math.floor(diffDays / 7) });
        return t('placeExplore.recentMonthsAgo', { n: Math.floor(diffDays / 30) });
    };

    // 모임 생성 확인 모달 (리스트/지도 모드 공용)
    const createConfirmModal = showCreateConfirm ? (() => {
        const allData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
        const p = pendingCreatePlace;
        const dateStr = allData.selectedDate
            ? new Date(allData.selectedDate).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })
            : '';
        return (
            <Sheet
                open
                title={t('placeExplore.confirmTitle')}
                closeLabel={t('placeExplore.back')}
                onClose={() => setShowCreateConfirm(false)}
            >
                <InfoGrid
                    columns={1}
                    items={[
                        {
                            label: t('placeExplore.confirmMeeting'),
                            value: allData.meetingName || t('placeExplore.defaultMeetingName'),
                        },
                        ...(dateStr ? [{ label: t('placeExplore.confirmDate'), value: dateStr }] : []),
                        ...(allData.selectedTime
                            ? [{ label: t('placeExplore.confirmTime'), value: allData.selectedTime }]
                            : []),
                        {
                            label: t('placeExplore.confirmPlace'),
                            value: p ? (
                                <>
                                    {p.name}
                                    {p.address && <span className={styles.subValue}>{p.address}</span>}
                                </>
                            ) : t('placeExplore.noPlaceLabel'),
                            muted: !p,
                        },
                        ...(allData.meetingType === 'TRAVEL' && allData.travelDestination
                            ? [{ label: t('placeExplore.confirmDestination'), value: allData.travelDestination }]
                            : []),
                        ...(allData.description
                            ? [{ label: t('placeExplore.confirmDescription'), value: allData.description }]
                            : []),
                    ]}
                />

                {/* 도착 레이스 — 선택 */}
                <div className={styles.raceBlock}>
                    <SwitchList>
                        <SwitchRow
                            label={t('placeExplore.raceUse')}
                            desc={t('placeExplore.raceDesc')}
                            checked={raceEnabled}
                            onChange={(next) => setRaceEnabled(next)}
                        />
                    </SwitchList>

                    {raceEnabled && (
                        <>
                            <p className={styles.label}>{t('placeExplore.racePenaltyMoney')}</p>
                            <div className={styles.stepper}>
                                <button
                                    type="button"
                                    className={styles.stepperBtn}
                                    onClick={() => setLateFeeAmount(v => Math.max(0, v - 1000))}
                                    aria-label={t('placeExplore.racePenaltyMoney')}
                                >
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                                        strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                                <span className={styles.stepperValue}>
                                    {t('meetingFinished.amountWon', { amount: lateFeeAmount.toLocaleString('ko-KR') })}
                                </span>
                                <button
                                    type="button"
                                    className={styles.stepperBtn}
                                    onClick={() => setLateFeeAmount(v => v + 1000)}
                                    aria-label={t('placeExplore.racePenaltyMoney')}
                                >
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                                        strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                            </div>

                            <p className={styles.label}>{t('placeExplore.racePenaltyLastLabel')}</p>
                            <div className={styles.options} role="radiogroup" aria-label={t('placeExplore.racePenaltyLastLabel')}>
                                {[
                                    { v: 'NONE', label: t('placeExplore.racePenaltyNone'), desc: t('placeExplore.racePenaltyNoneDesc') },
                                    { v: 'SHUTTLE', label: t('placeExplore.racePenaltyShuttle'), desc: t('placeExplore.racePenaltyShuttleDesc') },
                                    { v: 'SHOT', label: t('placeExplore.racePenaltyShot'), desc: t('placeExplore.racePenaltyShotDesc') },
                                    { v: 'BOTH', label: t('placeExplore.racePenaltyBoth'), desc: t('placeExplore.racePenaltyBothDesc') },
                                ].map(opt => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        role="radio"
                                        aria-checked={lastPlacePenaltyType === opt.v}
                                        className={lastPlacePenaltyType === opt.v
                                            ? `${styles.option} ${styles.optionOn}`
                                            : styles.option}
                                        onClick={() => {
                                            setLastPlacePenaltyType(opt.v as any);
                                            setShuttlePenaltyEnabled(opt.v === 'SHUTTLE' || opt.v === 'BOTH');
                                        }}
                                    >
                                        <span className={styles.optionLabel}>{opt.label}</span>
                                        <span className={styles.optionDesc}>{opt.desc}</span>
                                    </button>
                                ))}
                            </div>

                            <p className={styles.warn}>{t('placeExplore.raceWarning')}</p>
                        </>
                    )}
                </div>

                <p className={styles.note}>{t('placeExplore.confirmNote')}</p>
                <div className={styles.sheetActions}>
                    <Button variant="secondary" block onClick={() => setShowCreateConfirm(false)} disabled={isCreating}>
                        {t('placeExplore.back')}
                    </Button>
                    <Button block onClick={confirmCreateMeeting} disabled={isCreating}>
                        {isCreating ? t('placeExplore.createProgress') : t('placeExplore.create')}
                    </Button>
                </div>
            </Sheet>
        );
    })() : null;

    /* X 로 닫을 때 — 서브 장소를 고르던 중이면 그 창으로 돌아갑니다.
       기본 동작은 마지막 탭으로 보내 버려서, 고르다 닫으면 메인 화면으로
       튕겼습니다. 고르던 입력도 그대로 되살립니다. */
    const subPlaceClose = useCallback(() => {
        const st: any = location.state || {};
        navigate(st.returnTo || '/home', {
            replace: true,
            state: {
                fromSubPlaceModal: true,
                subPlaceForm: st.subPlaceForm,
                editingSubPlaceId: st.editingSubPlaceId,
                isCustomVisitTime: st.isCustomVisitTime,
                meetingId: st.meetingId,
            },
        });
    }, [location.state, navigate]);
    const onScreenClose = location.state?.subPlaceMode ? subPlaceClose : undefined;

    // 지도 모드 — 공통 PlaceMapView 사용
    if (mapMode) {
        return (
            <div className={isFlashMode ? `${styles.wrapper} ${styles.wrapperFlash}` : styles.wrapper}>
                {createConfirmModal}
                <AppBar
                    title={t('meetingCreate.placeSelect')}
                    mode="sub"
                    onClose={onScreenClose}
                    onBack={() => {
                        setMapMode(false);
                        setSelectedPlaceForConfirm(null);
                        setShowReviews(false);
                        setDetailOpen(true);
                    }}
                />

                <PlaceMapView
                    places={nearbyPlaces.filter(p => p.lat && p.lng).map(p => ({ ...p, lat: p.lat!, lng: p.lng! }))}
                    center={currentLocation || undefined}
                    selectedPlaceId={mapSelectedPlace?.id}
                    categories={CATEGORIES}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                    onPlaceSelect={(p) => {
                        setMapSelectedPlace(p as Place);
                        // 번개 후보 추가 모드 — 즉시 후보 등록 (선택 즉시 추가 → 정보 팝업 미표시)
                        if (isFlashAddMode) {
                            handleAddFlashCandidate(p as Place);
                            return;
                        }
                        // 번개 다중 선택 모드 — 정보 팝업도 같이 보여주되, 토글은 카드 안 CTA 로
                        // (자동 토글 X — 사용자가 정보 보고 [후보 추가]/[제외] 결정)
                        setSelectedPlaceForConfirm(p as Place);
                        setDetailOpen(true);
                        setShowReviews(false);
                        fetchReviews(p as Place);
                    }}
                    // onPlaceConfirm 의도적으로 미전달 — PlaceMapView 내부 "이 장소로 선택" 버튼 비활성
                    // overlay 의 "이 장소로 모임 만들기" 버튼이 유일한 확정 경로 (selectedPlaceForConfirm 기반)
                    onCenterChange={(c) => {
                        // '여기서 검색' 클릭 / 검색 결과 클릭 시 새 좌표 주변 추천 장소 재로딩
                        setCurrentLocation(c);
                        setLocationFailed(false);
                    }}
                    // 지도 영역 탭 시 정보 팝업 자동 닫기 (사이드 시트 접힘)
                    onMapClick={() => {
                        setDetailOpen(false);
                        setSelectedPlaceForConfirm(null);
                        setShowReviews(false);
                    }}
                    // 번개 모드: 하단 카드/디테일 패널 미사용 → setCenter latOffset 폭증 회피용으로 false 고정
                    detailOpen={isFlashMode ? false : detailOpen}
                    onVisiblePlacesChange={fetchPhotosForPlaces}
                    showSearch={true}
                    showBackButton={false}
                    showCategories={true}
                />

                {/* 장소 선택 확인 팝업 (지도 모드) */}
                {selectedPlaceForConfirm && (
                    <div className={!detailOpen ? `${styles.overlay} ${styles.overlayMin}` : styles.overlay}>
                        <div
                            ref={cardElRef}
                            className={detailOpen ? `${styles.detail} ${styles.isOpen}` : `${styles.detail} ${styles.isClosed}`}
                            onTouchStart={(e) => {
                                // 리뷰·사진처럼 스스로 스크롤/스와이프하는 영역은 시트 드래그를 막습니다.
                                // 클래스로 찾으면 CSS Modules 해시 때문에 조용히 어긋나므로 data 속성으로 표시합니다.
                                const target = e.target as HTMLElement;
                                isDragDisabled.current = !!target.closest('[data-drag-lock]');
                                dragStartY.current = e.touches[0].clientY;
                                dragStartX.current = e.touches[0].clientX;
                                dragCurrentY.current = 0;
                                dragCurrentX.current = 0;
                                if (!isDragDisabled.current && cardElRef.current) cardElRef.current.style.transition = 'none';
                            }}
                            onTouchMove={(e) => {
                                if (isDragDisabled.current) return;
                                const diffY = e.touches[0].clientY - dragStartY.current;
                                const diffX = e.touches[0].clientX - dragStartX.current;
                                dragCurrentY.current = diffY;
                                dragCurrentX.current = diffX;
                                // 사이드 시트 — 왼쪽으로 끄는 동안 따라가게
                                if (Math.abs(diffX) > Math.abs(diffY) && cardElRef.current && diffX < 0) {
                                    cardElRef.current.style.transform = `translateX(${diffX}px)`;
                                }
                            }}
                            onTouchEnd={() => {
                                if (isDragDisabled.current) { isDragDisabled.current = false; return; }
                                if (cardElRef.current) {
                                    cardElRef.current.style.transition = 'transform 0.3s ease';
                                    cardElRef.current.style.transform = '';
                                }
                                // 왼쪽으로 80px 이상 스와이프 → 닫기
                                if (dragCurrentX.current < -80 && Math.abs(dragCurrentX.current) > Math.abs(dragCurrentY.current)) {
                                    setSelectedPlaceForConfirm(null);
                                    setShowReviews(false);
                                }
                            }}
                        >
                            <button
                                className={styles.detailClose}
                                aria-label={t('placeExplore.collapse')}
                                onClick={() => { setSelectedPlaceForConfirm(null); setShowReviews(false); }}
                            >×</button>
                            {/* 좌우 스와이프 네비게이션 */}
                            {(() => {
                                const places = nearbyPlaces.filter(p => p.name);
                                const currentIdx = places.findIndex(p => p.id === selectedPlaceForConfirm.id);
                                return (
                                    <div className={styles.nav}>
                                        <button
                                            className={styles.navBtn}
                                            onClick={() => switchPlace('prev')}
                                            disabled={currentIdx <= 0}
                                        >‹</button>
                                        <span className={styles.navInfo}>{currentIdx + 1} / {places.length}</span>
                                        <button
                                            className={styles.navBtn}
                                            onClick={() => switchPlace('next')}
                                            disabled={currentIdx >= places.length - 1}
                                        >›</button>
                                    </div>
                                );
                            })()}
                            {!detailOpen && (
                                <div className={styles.detailMini} onClick={() => setDetailOpen(true)}>
                                    <IcPin size={14} /> {selectedPlaceForConfirm.name}
                                </div>
                            )}
                            {(() => {
                                const photoList = (selectedPlaceForConfirm.photos && selectedPlaceForConfirm.photos.length > 0)
                                    ? selectedPlaceForConfirm.photos
                                    : (selectedPlaceForConfirm.imageUrl ? [selectedPlaceForConfirm.imageUrl] : []);
                                if (photoList.length === 0) return null;
                                return <PhotoCarousel photos={photoList} alt={selectedPlaceForConfirm.name} />;
                            })()}
                            <div className={styles.info}>
                                <span className={styles.infoIcon}><CategoryIcon icon={selectedPlaceForConfirm.categoryIcon} size={18} /></span>
                                <div className={styles.infoBody}>
                                    <div className={styles.name}>{selectedPlaceForConfirm.name}</div>
                                    <div className={styles.ratingRow}>
                                        {selectedPlaceForConfirm.rating && (
                                            <span className={styles.rating}>
                                                {Array.from({ length: Math.round(selectedPlaceForConfirm.rating) }).map((_, i) => (
                                                    <IcStarFilled key={`f${i}`} size={14} color="#FBBF24" />
                                                ))}
                                                {Array.from({ length: 5 - Math.round(selectedPlaceForConfirm.rating) }).map((_, i) => (
                                                    <IcStarOutline key={`o${i}`} size={14} color="#D1D5DB" />
                                                ))}
                                                {' '}{selectedPlaceForConfirm.rating}
                                            </span>
                                        )}
                                        {selectedPlaceForConfirm.categoryLabel && (
                                            <span className={styles.catBadge}>{selectedPlaceForConfirm.categoryLabel}</span>
                                        )}
                                    </div>
                                    <div className={styles.addr}>{selectedPlaceForConfirm.address}</div>
                                    <div className={styles.meta}>
                                        {selectedPlaceForConfirm.phone && <span><IcCall size={13} /> {selectedPlaceForConfirm.phone}</span>}
                                    </div>
                                    {(selectedPlaceForConfirm.openingHoursList && selectedPlaceForConfirm.openingHoursList.length > 0) ? (
                                        <div className={showHoursDetail ? `${styles.hours} ${styles.isOpen}` : styles.hours}>
                                            <button className={styles.hoursToggle} onClick={() => setShowHoursDetail(v => !v)}>
                                                <span><IcClock size={14} /> {selectedPlaceForConfirm.openingHoursList[0]}</span>
                                                <span className={showHoursDetail ? `${styles.hoursArrow} ${styles.isOpen}` : styles.hoursArrow}><IcChevron size={14} /></span>
                                            </button>
                                            {showHoursDetail && (
                                                <ul className={styles.hoursList}>
                                                    {selectedPlaceForConfirm.openingHoursList.map((h, i) => (
                                                        <li key={i}>{h}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ) : selectedPlaceForConfirm.openingHours ? (
                                        <div className={styles.hours}>
                                            <span><IcClock size={14} /> {selectedPlaceForConfirm.openingHours}</span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className={styles.reviewsBlock} data-drag-lock="">
                                <button className={styles.reviewsOpen} onClick={() => setShowReviews(true)}>
                                    <span className={styles.reviewsOpenLabel}>
                                        <IcReview size={16} />
                                        <span>{t('placeExplore.reviewsTitle')}{reviews.length > 0 ? ` ${reviews.length}` : ''}</span>
                                    </span>
                                    <span className={styles.reviewsOpenArrow}><IcChevron size={14} /></span>
                                </button>
                                {/* 리뷰 미리보기 — 상위 3건. 누르면 블로그, 없으면 전체 리뷰.
                                    마크업은 아래 '리뷰 전체보기' 와 같은 클래스를 씁니다 —
                                    예전에는 여기만 인라인으로 둥근 회색 카드를 그려서
                                    같은 리뷰가 두 가지 모양으로 보였습니다. */}
                                {reviews.length > 0 && (
                                    <div className={styles.reviews}>
                                        {reviews.slice(0, 3).map((review: any, idx: number) => (
                                            <div
                                                key={idx}
                                                className={styles.review}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (review.link) openExternalUrl(review.link);
                                                    else setShowReviews(true);
                                                }}
                                            >
                                                {review.thumbnail && (
                                                    <div className={styles.reviewThumb}>
                                                        <img src={review.thumbnail} alt="" loading="lazy" />
                                                    </div>
                                                )}
                                                <div className={styles.reviewBody}>
                                                    <div className={styles.reviewTitle}>{review.title}</div>
                                                    <div className={styles.reviewDesc}>{review.description}</div>
                                                    <div className={styles.reviewMeta}>
                                                        <span>{review.bloggerName}</span>
                                                        {review.postDate && <span>{review.postDate}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={styles.confirmBtns}>
                                {isFlashMode ? (
                                    // 번개 모드 — 후보 토글 버튼으로 대체
                                    (() => {
                                        const isSelected = isFlashSelected(selectedPlaceForConfirm as any);
                                        return (
                                            <button
                                                className={styles.confirmBtn + ' ' + styles.confirmBtnPrimary}
                                                onClick={() => {
                                                    toggleFlashSelection(selectedPlaceForConfirm as any);
                                                }}
                                                style={isSelected ? { background: '#fff', color: '#1a73e8', border: '2px solid #1a73e8' } : undefined}
                                            >
                                                {isSelected ? '후보 선택됨 (제외하기)' : '+ 후보로 추가'}
                                            </button>
                                        );
                                    })()
                                ) : (
                                    <button className={styles.confirmBtn + ' ' + styles.confirmBtnPrimary} onClick={() => createMeetingAndNavigate(selectedPlaceForConfirm)} disabled={isCreating}>
                                        {isCreating ? t('placeExplore.creating') : (location.state?.subPlaceMode ? t('placeExplore.subPlaceSelect') : t('placeExplore.createHere'))}
                                    </button>
                                )}
                                <button className={styles.confirmBtn + ' ' + styles.confirmBtnSecondary} onClick={() => setDetailOpen(false)}>
                                    {t('placeExplore.collapse')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 번개 다중 선택 모드 — 지도 화면 하단 sticky 바 (선택 목록 + 다음 버튼 통합) */}
                {isFlashMode && (
                    <div className={styles.flashBar + ' ' + styles.flashBarMap}>
                        {flashSelected.length > 0 && (
                            <div className={styles.flashThumbs}>
                                {flashSelected.map(p => (
                                    <div key={p.id} className={styles.flashThumb}>
                                        {p.imageUrl ? (
                                            <img src={p.imageUrl} alt={p.name} loading="lazy" />
                                        ) : (
                                            <div className={styles.flashThumbEmpty}><IcPin size={14} /></div>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.flashThumbRemove}
                                            aria-label={t('cost.delete')}
                                            onClick={(e) => { e.stopPropagation(); toggleFlashSelection(p); }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className={styles.flashRow}>
                            <div className={styles.flashInfo}>
                                <div className={styles.flashCount}><IcBolt size={14} /> {t('placeExplore.flashSelectCount', { n: flashSelected.length, max: FLASH_MAX })}</div>
                                <div className={styles.flashNames}>
                                    {flashSelected.map(p => p.name).join(', ') || t('placeExplore.flashSelectEmpty')}
                                </div>
                            </div>
                            <button
                                className={styles.flashNext}
                                onClick={proceedFlash}
                                disabled={flashSelected.length < 1}
                            >
                                {t('placeExplore.flashNext')} →
                            </button>
                        </div>
                    </div>
                )}

                {/* 리뷰 풀스크린 모달 (지도 모드) */}
                {showReviews && selectedPlaceForConfirm && (
                    <div className={styles.revOverlay} onClick={() => setShowReviews(false)}>
                        <div className={styles.revModal} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.revHead}>
                                <div className={styles.revTitle}>
                                    <IcReview size={18} />
                                    <span>{selectedPlaceForConfirm.name}</span>
                                    <span className={styles.revCount}>{reviews.length > 0 ? t('placeExplore.reviewCount', { count: reviews.length }) : ''}</span>
                                </div>
                                <button className={styles.revClose} aria-label={t('placeExplore.collapse')} onClick={() => setShowReviews(false)}>×</button>
                            </div>
                            <div className={styles.revBody}>
                                {isReviewLoading ? (
                                    <div className={styles.state}>{t('placeExplore.reviewsLoading')}</div>
                                ) : reviews.length === 0 ? (
                                    <div className={styles.state}>{t('placeExplore.reviewsEmpty')}</div>
                                ) : (
                                    <div className={styles.reviews}>
                                        {reviews.map((review, idx) => (
                                            <div key={idx} className={styles.review} onClick={() => review.link && openExternalUrl(review.link)}>
                                                {review.thumbnail && (
                                                    <div className={styles.reviewThumb}><img src={review.thumbnail} alt="" /></div>
                                                )}
                                                <div className={styles.reviewBody}>
                                                    <div className={styles.reviewTitle}>{review.title}</div>
                                                    <div className={styles.reviewDesc}>{review.description}</div>
                                                    <div className={styles.reviewMeta}>
                                                        <span>{review.bloggerName}</span>
                                                        {review.postDate && <span>{review.postDate}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // 리스트 모드
    /* 장소 확인 시트 · 번개 하단바 · 리뷰 전체보기 —
       목록 모드와 지도 모드가 똑같이 쓰므로 한 번만 만들어 둡니다. */

    const placeConfirmSheet = (
        <Sheet
            open={!!selectedPlaceForConfirm}
            title={selectedPlaceForConfirm?.name || ''}
            closeLabel={t('common.close')}
            onClose={() => { setSelectedPlaceForConfirm(null); setShowReviews(false); }}
        >
            {selectedPlaceForConfirm && (
                <>
                    {selectedPlaceForConfirm.imageUrl && (
                        <img
                            className={styles.sheetPhoto}
                            src={selectedPlaceForConfirm.imageUrl}
                            alt=""
                            loading="lazy"
                        />
                    )}
                    <div className={styles.sheetTags}>
                        {selectedPlaceForConfirm.rating ? (
                            <Badge tone="outline">{selectedPlaceForConfirm.rating}</Badge>
                        ) : null}
                        {selectedPlaceForConfirm.categoryLabel && (
                            <Badge tone="neutral">{selectedPlaceForConfirm.categoryLabel}</Badge>
                        )}
                    </div>
                    {selectedPlaceForConfirm.address && (
                        <p className={styles.sheetAddr}>{selectedPlaceForConfirm.address}</p>
                    )}
                    {(selectedPlaceForConfirm.openingHours || selectedPlaceForConfirm.phone) && (
                        <p className={styles.sheetMeta}>
                            {selectedPlaceForConfirm.openingHours
                                ? selectedPlaceForConfirm.openingHours.split(';')[0]
                                : ''}
                            {selectedPlaceForConfirm.openingHours && selectedPlaceForConfirm.phone ? ' · ' : ''}
                            {selectedPlaceForConfirm.phone || ''}
                        </p>
                    )}

                    <ActionList>
                        <ActionRow
                            standalone
                            title={t('placeExplore.reviewsTitle')}
                            value={reviews.length > 0 ? String(reviews.length) : undefined}
                            onClick={() => setShowReviews(true)}
                        />
                    </ActionList>

                    <div className={styles.sheetActions}>
                        <Button
                            block
                            disabled={isCreating}
                            onClick={() => createMeetingAndNavigate(selectedPlaceForConfirm)}
                        >
                            {isCreating
                                ? t('placeExplore.creating')
                                : (location.state?.subPlaceMode
                                    ? t('placeExplore.selectPlace')
                                    : t('placeExplore.createWithPlace'))}
                        </Button>
                        <Button
                            variant="secondary"
                            block
                            onClick={() => handleOpenMap(selectedPlaceForConfirm)}
                        >
                            {t('placeExplore.viewOnMap')}
                        </Button>
                    </div>
                </>
            )}
        </Sheet>
    );

    const flashStickyBar = isFlashMode ? (
        <ButtonFooter>
            <span className={styles.flashInfo}>
                <span className={styles.flashCount}>
                    {t('placeExplore.flashSelectCount', { n: flashSelected.length, max: FLASH_MAX })}
                </span>
                <span className={styles.flashNames}>
                    {flashSelected.map(p => p.name).join(', ') || t('placeExplore.flashSelectEmpty')}
                </span>
            </span>
            <Button onClick={proceedFlash} disabled={flashSelected.length < 1}>
                {t('placeExplore.flashNext')}
            </Button>
        </ButtonFooter>
    ) : null;

    const reviewFullModal = (
        <Sheet
            open={showReviews && !!selectedPlaceForConfirm}
            title={selectedPlaceForConfirm?.name || t('placeExplore.reviewsTitle')}
            closeLabel={t('placeExplore.collapse')}
            onClose={() => setShowReviews(false)}
        >
            {isReviewLoading ? (
                <p className={styles.state}>{t('placeExplore.reviewsLoading')}</p>
            ) : reviews.length === 0 ? (
                <p className={styles.state}>{t('placeExplore.reviewsEmpty')}</p>
            ) : (
                <ul className={styles.reviews}>
                    {reviews.map((review, idx) => (
                        <li key={idx}>
                            <button
                                type="button"
                                className={styles.review}
                                onClick={() => review.link && openExternalUrl(review.link)}
                            >
                                {review.thumbnail && (
                                    <span className={styles.reviewThumb}>
                                        <img src={review.thumbnail} alt="" loading="lazy" />
                                    </span>
                                )}
                                <span className={styles.reviewBody}>
                                    <span className={styles.reviewTitle}>{review.title}</span>
                                    <span className={styles.reviewDesc}>{review.description}</span>
                                    <span className={styles.reviewMeta}>
                                        {[review.bloggerName, review.postDate, review.genre, review.budget]
                                            .filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Sheet>
    );
    return (
        <AppScreen title={t('meetingCreate.placeSelect')} bar="sub" onClose={onScreenClose}>
            <LocationLoading visible={isLocationLoading} />
            {createConfirmModal}

            {/* 위치를 못 잡았을 때 — 시청 같은 엉뚱한 좌표로 떨어뜨리지 않고 다시 시도하게 합니다 */}
            {locationFailed && (
                <Section spacing="tight">
                    <ActionList>
                        <ActionRow
                            standalone
                            title={t('placeExplore.locationFailedTitle')}
                            desc={t('placeExplore.locationFailedSub')}
                            value={t('placeExplore.retryBtn')}
                            onClick={handleRetryLocation}
                        />
                    </ActionList>
                </Section>
            )}

            {/* 모임 만들기 3단계 — sub_place 추가·번개 흐름에서는 단계가 다르므로 숨깁니다 */}
            {!location.state?.subPlaceMode && !isFlashMode && !isFlashAddMode && (
                <Section spacing="tight">
                    <MeetingProgress currentStep={3} isTravel={false} />
                </Section>
            )}

            {/* 번개 — 고른 곳 */}
            {isFlashMode && flashSelected.length > 0 && (
                <Section spacing="tight">
                    <SectionHeader
                        title={t('placeExplore.flashSelectCount', { n: flashSelected.length, max: FLASH_MAX })}
                    />
                    <CardRail>
                        {flashSelected.map(p => (
                            <PlaceCard
                                key={p.id}
                                name={p.name}
                                imageUrl={p.imageUrl}
                                badge={{ label: t('cost.delete'), tone: 'neutral' }}
                                onClick={() => toggleFlashSelection(p)}
                            />
                        ))}
                    </CardRail>
                </Section>
            )}

            {/* 어디서 찾을지 */}
            <Section spacing="tight">
                <button type="button" className={styles.search} onClick={handleDirectSearch}>
                    <span className={styles.searchIcon}><IcSearch size={18} /></span>
                    {t('placeExplore.searchPlaceholder')}
                </button>
                <div className={styles.here}>
                    <span className={styles.hereName}>{locationName}</span>
                    {(nearbyPlaces.length > 0 || isFlashMode || isFlashAddMode) && (
                        <button type="button" className={styles.hereBtn} onClick={() => openMapOrNative()}>
                            {t('placeExplore.mapBtn')}
                        </button>
                    )}
                </div>
                <button type="button" className={styles.skip} onClick={handleSkip}>
                    {t('placeExplore.skipPrompt')}
                </button>
            </Section>

            {/* 추천 모임에서 이미 고른 장소가 있을 때 */}
            {savedMeetingData.selectedPlaceInfo && (
                <Section divided>
                    <SectionHeader title={t('placeExplore.selectedPlace')} />
                    <div className={styles.picked}>
                        <span className={styles.pickedName}>{savedMeetingData.selectedPlaceInfo.name}</span>
                        <span className={styles.pickedAddr}>{savedMeetingData.selectedPlaceInfo.address}</span>
                    </div>
                    <Button
                        block
                        onClick={() => createMeetingAndNavigate({
                            id: 'preselected',
                            name: savedMeetingData.selectedPlaceInfo.name,
                            address: savedMeetingData.selectedPlaceInfo.address,
                            lat: savedMeetingData.selectedPlaceInfo.lat,
                            lng: savedMeetingData.selectedPlaceInfo.lng,
                        })}
                    >
                        {location.state?.subPlaceMode ? t('placeExplore.selectPlace') : t('placeExplore.createWithPlace')}
                    </Button>
                </Section>
            )}

            {/* 지도 미리보기 — 후보들이 어디에 흩어져 있는지 먼저 감을 잡게.
                누르면 지금 쓰던 전체 지도로 들어갑니다. */}
            {currentLocation && !isFlashAddMode && (
                <Section spacing="tight">
                    <Bleed>
                        <div
                            className={styles.mapPreview}
                            role="button"
                            tabIndex={0}
                            onClick={handleDirectSearch}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleDirectSearch(); }}
                        >
                            <MapContainer
                                center={currentLocation}
                                markers={nearbyPlaces
                                    .filter(p => p.lat && p.lng)
                                    .slice(0, 12)
                                    .map(p => ({ id: p.id, lat: p.lat!, lng: p.lng!, label: '', tooltip: p.name }))}
                                height={180}
                                zoomLevel={5}
                                fitBounds={false}
                            />
                        </div>
                    </Bleed>
                </Section>
            )}

            {/* 기준 줄 — 무엇을 기준으로, 어떤 순서로 보고 있는지 */}
            <Section spacing="tight">
                <FilterBar
                    options={CATEGORIES.map(c => ({ value: c.id, label: c.label }))}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    ariaLabel={t('placeExplore.categoryLabel')}
                    sortLabel={sortBy === 'near' ? t('placeExplore.sortNear') : t('placeExplore.sortRating')}
                    onSort={() => setShowSortSheet(true)}
                />
                {currentLocation && (
                    <p className={styles.anchorNote}>
                        {t('placeExplore.anchorLabel')} · {anchorName}
                    </p>
                )}
            </Section>

            {/* 추천 — 모드별 3섹션 ({일반·관광·숙박} × {국내·해외}) */}
            {(() => {
                const kind: 'hotel' | 'attraction' | 'general' =
                    hotelOnlyMode ? 'hotel' : (isTravel ? 'attraction' : 'general');
                const mId = (location.state as any)?.meetingId;

                const distLabel = (p: Place) => {
                    if (!currentLocation || !p.lat || !p.lng) return p.distance || '';
                    const km = haversineMeters(currentLocation.lat, currentLocation.lng, p.lat, p.lng) / 1000;
                    return km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
                };
                const metaOf = (p: Place) => {
                    const rc = (p as any).userRatingsTotal || (p as any).reviewCount;
                    return [
                        (p as any).categoryLabel || '',
                        p.rating ? `${p.rating}` : '',
                        rc ? t('placeExplore.reviewCount', { count: rc }) : '',
                        distLabel(p),
                        formatLastUsed(p.lastUsed),
                    ].filter(Boolean).join(' · ');
                };
                /* 왜 이 장소가 이 섹션에 있는지 — 카드에 한 줄로 답니다.
                   섹션 제목에만 근거가 있으면 스크롤 중에는 그냥 목록으로 읽힙니다. */
                const whyOf = (p: Place, reason: 'recent' | 'frequent' | 'nearby'): string | undefined => {
                    if (reason === 'frequent' && p.visitCount && p.visitCount > 1) {
                        return t('placeExplore.whyFrequent', { n: p.visitCount });
                    }
                    if (reason === 'recent') {
                        const when = formatLastUsed(p.lastUsed);
                        return when ? t('placeExplore.whyRecent', { when }) : undefined;
                    }
                    const rc = (p as any).userRatingsTotal || (p as any).reviewCount;
                    if (p.rating && rc) return t('placeExplore.whyRated', { rating: p.rating, count: rc });
                    const d = distLabel(p);
                    return d ? t('placeExplore.whyNear', { dist: d }) : undefined;
                };

                /* 가로 레일.
                   세로 목록이면 분류 하나에 다섯 줄씩 쌓여 계속 내려야 했습니다.
                   한 분류가 한 줄이면 여러 분류를 한 화면에서 훑고 좌우로 넘깁니다.
                   (예전에 세로로 바꿨던 이유 둘 — 카드 폭이 제각각, 화면 밖 사진
                    미로딩 — 은 각각 min-width:0 과 선제 로드 이펙트로 해결됐습니다.
                    사진이 없는 곳은 PlaceCard 가 그 자리의 지도를 깔아 줍니다.) */
                const section = (title: string, arr: Place[], reason: 'recent' | 'frequent' | 'nearby') => {
                    const list = arr.filter(p => p.name && !isExcludedFlashCand(p));
                    if (list.length === 0) return null;
                    const shown = expandedSections[title] ? list : list.slice(0, RAIL_PREVIEW);
                    return (
                        <Section divided key={title}>
                            <SectionHeader title={title} count={list.length} />
                            <CardRail>
                                {shown.map(p => (
                                    <PlaceCard
                                        key={p.id}
                                        name={p.name}
                                        meta={metaOf(p)}
                                        imageUrl={p.imageUrl}
                                        at={{ lat: p.lat, lng: p.lng }}
                                        evidence={whyOf(p, reason)}
                                        onClick={() => handleSelectPlace(p)}
                                    />
                                ))}
                            </CardRail>
                            {list.length > shown.length && (
                                <Button
                                    block
                                    variant="secondary"
                                    onClick={() => {
                                        setExpandedSections(v => ({ ...v, [title]: true }));
                                        // 펼치면서 새로 보이는 카드의 사진도 같이 받습니다.
                                        // (선제 로드는 앞 20건까지라 그 뒤는 빈 네모가 됩니다)
                                        fetchPhotosForPlaces(list.slice(shown.length).map(p => p.id));
                                    }}
                                >
                                    {t('common.more')}
                                </Button>
                            )}
                        </Section>
                    );
                };

                const nearbyClean = nearbyPlaces
                    .filter(p => p.name && !isExcludedFlashCand(p))
                    .slice()
                    .sort((a, b) => {
                        if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
                        if (!currentLocation) return 0;
                        const da = a.lat && a.lng
                            ? haversineMeters(currentLocation.lat, currentLocation.lng, a.lat, a.lng) : Infinity;
                        const db = b.lat && b.lng
                            ? haversineMeters(currentLocation.lat, currentLocation.lng, b.lat, b.lng) : Infinity;
                        return da - db;
                    });
                const recentLabel = kind === 'hotel' ? t('placeExplore.secRecentStay')
                    : kind === 'attraction' ? t('placeExplore.secRecentDest')
                        : t('placeExplore.recent');
                const nearbyLabel = kind === 'hotel' ? t('placeExplore.secPopularStay')
                    : kind === 'attraction' ? t('placeExplore.secAttraction')
                        : t('placeExplore.popular');

                return (
                    <>
                        {section(recentLabel, recentPlaces, 'recent')}
                        {kind === 'general' && mId
                            ? section(t('placeExplore.secFrequent'), frequentPlaces, 'frequent')
                            : null}
                        {isNearbyLoading && nearbyClean.length === 0 ? (
                            <Section divided>
                                <p className={styles.state}>{t('placeExplore.searchingNearby')}</p>
                            </Section>
                        ) : nearbyClean.length === 0 ? (
                            <Section divided>
                                <EmptyState
                                    title={!currentLocation
                                        ? t('placeExplore.noLocation')
                                        : isFlashAddMode
                                            ? t('flash.addPlaceConfirm.noMore')
                                            : t('placeExplore.loadFailed')}
                                    action={
                                        <Button onClick={handleDirectSearch}>
                                            {t('placeExplore.searchOnMap')}
                                        </Button>
                                    }
                                />
                            </Section>
                        ) : section(nearbyLabel, nearbyClean, 'nearby')}
                    </>
                );
            })()}

            {/* 추천이 끝난 뒤에만 광고를 둡니다. 추천 사이에 끼우면
                근거 뱃지 전체가 못 믿을 것이 됩니다.
                (데이터가 없으면 아무것도 그리지 않습니다) */}
            <Section divided>
                <SponsoredPlaces places={sponsoredPlaces} />
            </Section>

            <Section divided last>
                <Button block icon={<IcSearch size={16} />} onClick={handleDirectSearch}>
                    {t('placeExplore.searchOnMap')}
                </Button>
            </Section>

            {/* 정렬 고르기 */}
            <Sheet
                open={showSortSheet}
                title={t('placeExplore.sortLabel')}
                closeLabel={t('common.close')}
                onClose={() => setShowSortSheet(false)}
            >
                <ActionList>
                    {([['near', t('placeExplore.sortNear')], ['rating', t('placeExplore.sortRating')]] as const).map(([v, label]) => (
                        <ActionRow
                            key={v}
                            title={label}
                            desc={sortBy === v ? t('common.selected') : undefined}
                            onClick={() => { setSortBy(v as 'near' | 'rating'); setShowSortSheet(false); }}
                        />
                    ))}
                </ActionList>
            </Sheet>

            {/* 장소 확인 시트 */}
            {placeConfirmSheet}

            {/* 번개 다중 선택 — 하단 고정 바 */}
            {flashStickyBar}

            {/* 리뷰 전체 보기 */}
            {reviewFullModal}
        </AppScreen>
    );
};
